<template>
<div :class="{'container': isSettingsComponent}">

	<div v-if="$store.accountSetupMode" class="alert alert-info">
		<h2>Create a Password</h2>

		<p>This is your account settings page. Please set a password. You can also set some account privacy and preference settings if you choose.</p>

		<div v-if="$store.companySetupMode">
			<p>When you're happy with your account settings, click the button below and we'll move onto setting up your company.</p>

			<router-link class="btn btn-light" :to="nextStepsLink">
				<i class="fa fa-check"></i>
				Ok, Next Steps
			</router-link>
		</div>
	</div>

	<h2>
		Account Settings

		<button v-on:click="logoutAll" class="btn btn-outline-danger btn-sm" v-if="$store.userDetails.slug == $route.params.id">
			<i class="fa fa-sign-out-alt"></i>
			Log Me Out of All Devices & Services
		</button>
	</h2>

	<div v-if="$store.employee.email != $store.employee.email_verified && $store.userDetails.id == $store.employee.id" class="text-center">
		<div class="alert-warning alert">
			
			<template v-if="!verificationEmailSent">
				<p>Your email address is not verified. Click the button below to send a verification link to {{$store.userDetails.email}}</p>

				<button class="btn btn-light" v-on:click="sendEmailVerificationLink" type="button">
					<i class="fa fa-paper-plane"></i>
					Send Verification Email
				</button>
			</template>
			<div v-else>Please check your email for a verification link</div>
		</div>
	</div>
	
	<widget class="form">
		<vue-form v-on:submit="changePassword" size="sm" save-button-text="Change Password" :no-top-button="true">
		
			<h3>Change Password</h3>

			<field label="New Password" col-class="col-md-4" v-model="password" :params="{type:'password'}"></field>

			<field label="Confirm Password" col-class="col-md-4" v-model="confirmPassword" :params="{type:'password'}"></field>
		
		</vue-form>
	</widget>

	<widget class="form">
		<vue-form :save-buttons-hidden="true" size="sm">

			<h3>Preferences & Privacy</h3>

			<field 
				label="Email me unread messages" 
				col-class="col-6 col-sm-6" 
				:params="{type:'boolean'}" 
				:control-left="true"
				v-model="$store.employee.send_message_emails"
				v-on:change="updateEmailSetting">
			</field>

			<field 
				label="Send me emails with helpful articles and tips" 
				col-class="col-6 col-sm-6" 
				:params="{type:'boolean'}" 
				:control-left="true"
				v-model="$store.employee.send_marketing_emails"
				v-on:change="updateEmailSetting">
			</field>

			<field
				label="Send me emails related to service requests"
				col-class="col-6 col-sm-6"
				:params="{type:'boolean'}"
				:control-left="true"
				v-model="$store.employee.send_service_request_emails"
				v-on:change="updateEmailSetting">
			</field>

			<!--
			<field 
				label="Send me emails from Ontario Construction News" 
				col-class="col-6 col-sm-6" 
				:params="{type:'boolean'}" 
				:control-left="true"
				v-model="subscriptions.ocn_subscribed"
				v-on:change="updateOCNSettings">
			</field>
			-->

			<field 
				label="Email Address Public" 
				col-class="col-6 col-sm-6" 
				:params="{type:'boolean'}" 
				:control-left="true"
				v-model="$store.employee.public_email"
				v-on:change="updatePrivacySetting('public_email')">
			</field>

			<field 
				label="Phone # Public" 
				col-class="col-6 col-sm-6" 
				:params="{type:'boolean'}" 
				:control-left="true"
				v-model="$store.employee.public_phone"
				v-on:change="updatePrivacySetting('public_phone')">
			</field>

			<field 
				v-if="$store.userDetails.slug == $route.params.id"
				label="Lock Profile" 
				col-class="col-6 col-sm-6" 
				:control-left="true"
				:params="{type:'btn-group',
					buttons:[
						{icon:'fa-lock fa-fw',value:1},
						{icon:'fa-lock-open fa-fw',value:0}
					],
					disableNull: true
				}" 
				v-model="$store.employee.editing_locked"
				v-on:change="changeLock">
			</field>

			<p v-if="$store.employee.editing_locked == 1"><strong>Locked: </strong>Only you may edit your profile or change your account settings</p>

			<p v-else><strong>Unlocked: </strong>You are allowing your employer to edit your profile and change your account settings on your behalf</p>

			<field 
				v-if="$store.userDetails.slug == $route.params.id"
				:label="'Allow '+$store.appName+' support personnel to access your account'" 
				col-class="col-6 col-sm-6" 
				:control-left="true"
				:params="{type:'btn-group',
					buttons:[
						{icon:'fa-lock fa-fw',value:1},
						{icon:'fa-lock-open fa-fw',value:0}
					],
					disableNull: true
				}" 
				v-model="$store.employee.admin_lockout"
				v-on:change="changeAdminLockout">
			</field>
			<p v-if="$store.employee.admin_lockout == 1"><strong>Locked: </strong>{{$store.appName}} support staff will <em>not</em> be able to login to your account. This will prevent unauthorized access but may make technical support difficult.</p>

			<p v-else><strong>Unlocked: </strong>{{$store.appName}} support staff will be able to login to your account. You will receive an email when your account has been accessed</p>

		</vue-form>
	</widget>

	<widget v-if="$store.userDetails.slug == $route.params.id" class="form">
		<vue-form :save-buttons-hidden="true" size="sm">

			<h3>Device Settings</h3>

			<div class="alert" :class="{'alert-success':locationEnabled,'alert-warning':!locationEnabled}">
				<i class="fa fa-dot-circle-o"></i>

				<span v-if="locationEnabled">Location permissions enabled on this device</span>
				<div v-else>
					<p>Location permissions not enabled on this device. Some Clock In functions won't work</p>

					<div>
						<button class="btn btn-primary btn-sm" v-on:click="promptLocationPerms">
							Enable Location Permissions
						</button>
					</div>
				</div>
			</div>

			<div class="alert" :class="{'alert-success':notificationsEnabled,'alert-warning':!notificationsEnabled}">
				
				<p>
					<i class="fa fa-bell mr-1"></i>

					<span v-if="notificationsEnabled">Push notification permissions are enabled on this device</span>
					<span v-else>Push notification permissions are disabled on this device. You may not receive messages or updates in a timely manner</span>
				</p>

				<p><div class='onesignal-customlink-container'></div></p>

				<!--<p v-if="numDevicesWithPush > 0">
					You have registered for push notifications on {{numDevicesWithPush}} device{{numDevicesWithPush != 1 ? 's' : '' }}
				</p>-->

				<p v-if="thisDeviceRegisteredForNotifications">
					This device will receive notifications from your {{$store.appName}} account
				</p>

				<button class="btn btn-light btn-sm" type="button" v-on:click="testPush">
					Send a Test Notification
				</button>

				<button class="btn btn-light btn-sm" type="button" v-on:click="promptNotificationPerms">
					Prompt Notification Permission
				</button>
			</div>

		</vue-form>
	</widget>

	<widget class="form">
		<vue-form :save-buttons-hidden="true" size="sm">

			<h3>Profile QR Code</h3>

			<div class="mt-4">
				<vue-qr-code
					:params="{
						text: buildRedirectLink('user-profile', $store.userDetails.slug),
						width: 250,
						height: 250
					}"
					logo-url="/resources/icons/logo_dark_icon_192.png"
					logo-classes="p-1 rounded"
					:title="codeTitle"
					:show-copy-button="true">
				</vue-qr-code>
			</div>

		</vue-form>
	</widget>

	<widget v-if="$store.userDetails.slug == $route.params.id" class="form">
		<h3>Authentication Tokens</h3>

		<p>In order to enable a 3rd party application to access Tradetraks, you can create an Authentication Token, and then share that authentication token with the application</p>

		<!--
		<vue-form :save-buttons-hidden="true" size="sm">

			<field 
				label="API Key" 
				col-class="col-3 col-sm-3" 
				v-model="$store.userDetails.api_key"
				:readonly="true">

				<p>This key can be used for integration to 3rd party applications.</p>

				<div class="d-flex flex-row align-items-center">
					<button v-on:click="logoutAll" class="btn btn-outline-danger btn-sm mr-2">
						<i class="fa fa-sync"></i>
						Generate New API Key
					</button>

					<div>This will log you out of all devices & services</div>
				</div>

			</field>

		</vue-form>
		-->

		<table-list record-name="Authentication Token"
			:fields="[
					{label: 'App', key: 'label'},
					{label: 'Token', key: 'token'},
					{label: 'Expiry', key: 'expiryFormatted'},
					{label: 'Scope', key: 'scopeFormatted'}
				]"
			:records="mutatedAppAuthTokens"
			:has-delete="true"
			delete-endpoint="UserToken/delete"
			v-on:delete="fetchCreatedUserTokens">

			<button class="btn btn-primary" v-on:click="newAuthToken">Create</button>

		</table-list>

	</widget>

	<widget v-if="$store.userDetails.slug == $route.params.id" class="form">
		<vue-form :save-buttons-hidden="true" size="sm">

			<h3>Clear Cache</h3>
			<p class="text-muted">This will clear your current device's cache for TradeTraks, and log you out on this device.</p>
			<button class="btn btn-danger" v-on:click="clearCache">
				<i class="fa fa-trash"></i>
				Clear Cache
			</button>

		</vue-form>
	</widget>

	<modal-dialog :modal-visible="authTokenEditModal.visible"
		title="New Authentication Token"
		v-on:closeModal="authTokenEditModal.visible = false"
		v-on:confirm="saveAuthToken">

		<field label="App Label"
			col-class="col-md-4"
			v-model="authTokenEditModal.payload.label">
		</field>

		<field label="Allow full permissions of my account"
			col-class="col-auto"
			:control-left="true"
			:params="{type: 'boolean'}"
			v-model="authTokenEditModal.fullScope">
		</field>

		<template v-if="!authTokenEditModal.fullScope">
			<p>
				You may select specific resources to scope this authentication to. Remember that these scopes are still subject to your own user permissions provided to you by your employer.</p>

			<p>For more information on these resources, please visit the <a :href="apiRoot + 'docs/resources'" target="_blank">API Documentation</a></p>

			<field 
					label="Scope"
					col-class="stacked"
					:params="{
						type: 'checklist',
						options: resourcesOptions
					}"
					v-model="authTokenEditModal.scope">
			</field>
		</template>

	</modal-dialog>

</div>
</template>

<script>
import Field from "@/components/Field"
import VueForm from "@/components/VueForm"

import ModalDialog from "@/components/ModalDialog"
import Widget from "@/components/Widget"
import VueQrCode from "@/components/VueQrCode"
import TableList from "@/components/TableList"

export default {

	name: 'AccountSettings',
	components: {
		Field,
		VueForm,
		ModalDialog,
		Widget,
		VueQrCode,
		TableList
	},
	props: ["isSettingsComponent"],
	data() {
		return {
			password: null,
			confirmPassword: null,
			locationEnabled: false,
			//deviceId: null,
			registeredOneSignalExternalId: null,
			subscriptions: {
				ocn_subscribed: false
			},
			verificationEmailSent: false,
			appAuthTokens: [],
			authTokenEditModal: {
				visible: false,
				payload: {},
				fullScope: true,
				scope: [],
				resources: []
			}
		}
	},
	computed: {
		codeTitle() {
			let tokens = []
			if (this.$store.userDetails) {
				if ('first_name' in this.$store.userDetails && this.$store.userDetails.first_name)
					tokens.push(this.$store.userDetails.first_name)
				if ('last_name' in this.$store.userDetails && this.$store.userDetails.last_name)
					tokens.push(this.$store.userDetails.last_name)
			}

			let title = tokens.join(' ')
			if (this.$store.company && 'company_name' in this.$store.company && this.$store.company.company_name)
				title += " - " + this.$store.company.company_name

			return title   // "Cameron Arnold - Fairway Electrical Services Incorporated"
		},
		nextStepsLink() {
			if (this.$store.companySetupMode)
				return '/company/details'
			else
				return '/dashboard'
		},
		notificationsEnabled() {
			if ("Notification" in window)
				return Notification.permission == 'granted'
			else
				return false
		},
		/*numDevicesWithPush() {
			if (this.$store.userDetails.one_signal_user_id != "") {
				try {
					return JSON.parse(this.$store.userDetails.one_signal_user_id).length
				}
				catch (ex) {
					return 1
				}
			}
			else
				return 0
		},*/
		thisDeviceRegisteredForNotifications() {
			/*let ids = []
			try {
				ids = JSON.parse(this.$store.userDetails.one_signal_user_id)
			}
			catch (ex) {
				ids = []
			}

			if (ids.length > 0 && ids.indexOf(this.deviceId) >= 0)
				return true
			else
				return false*/

			if (this.registeredOneSignalExternalId && this.registeredOneSignalExternalId == this.$store.userDetails.slug)
				return true

			return false
		},
		mutatedAppAuthTokens() {
			let vm = this

			return this.appAuthTokens.map(token => {
				token.expiryFormatted = vm.formatDateTimeToLocal(token.expiry)

				token.scopeFormatted = ""
				try {
					let scopeObj = JSON.parse(token.scope_json)

					let hasMissingResource = false
					//if scope contains all models, just label it as "All"
					vm.authTokenEditModal.resources.forEach(resource => {
						let resourceIsPresent = scopeObj.find(scopedResource => scopedResource == resource.name)

						if (!resourceIsPresent)
							hasMissingResource = true
					})
					
					if (!hasMissingResource)
						token.scopeFormatted = "All"
					else
						token.scopeFormatted = scopeObj.join(", ")
				}
				catch (e) {}

				return token
			})
		},
		modules() {
			let vm = this

			let modules = []
			this.authTokenEditModal.resources.forEach(resource => {
				if (modules.indexOf(resource.module) == -1)
					modules.push(resource.module)
			})

			return modules
		},
		resourcesOptions() {
			return this.authTokenEditModal.resources.map(resource => {
				return {
					text: resource.name,
					value: resource.name
				}
			})
		}
	},
	methods: {
		clearCache() {
			localStorage.clear()
		},
		promptNotificationPerms() {
			Notification.requestPermission()
		},
		sendEmailVerificationLink() {
			let vm = this

			this.postData("User/send-email-verification",{}).then(response => {
				if (response.success) {
					vm.verificationEmailSent = true
					vm.alertSuccess("Email verification link sent!")
				}
			})
		},
		testPush() {
			let vm = this

			this.postData("User/test-notification",{}).then(response => {
				if (response.success)
					vm.alertSuccess("Test notification sent")
			})
		},
		promptLocationPerms() {
			let vm = this

			vm.getLocation().then().catch()

			let checkerTimer = setInterval(() => {
				navigator.geolocation && navigator.permissions.query({name:'geolocation'}).then(status => {
					if (status.state == 'granted') {
						vm.locationEnabled = true
						clearInterval(checkerTimer)
					}
					else
						vm.locationEnabled = false

				})
			},1000)
		},
		changePassword() {
			let vm = this

			//check that passwords match
			if (this.password == this.confirmPassword) {
				let payload = {
					password: this.password,
					user_slug: this.$route.params.id
				}

				this.postData("User/change-password",payload,true).then(response => {
					if (response.success == true) {
						vm.alertSuccess("Password changed!")
						vm.password = ""
						vm.confirmPassword = ""

						if (vm.$store.userDetails.tenant_id == 0) {
							//redirect to employers page
							vm.$router.push("/employers")
						}
						else if (vm.$store.accountSetupMode && vm.$store.companySetupMode) {
							vm.$store.accountSetupMode = false
							vm.$router.push("/setup")
						}
						else if (vm.$store.accountSetupMode && !vm.$store.companySetupMode) {
							vm.$store.accountSetupMode = false
							vm.$store.onboardingTips.clockIn = true
							vm.$router.push("/")
						}
					}
				})

			}
			else 
				this.alertFail("Passwords don't match!")
			
		},
		updateEmailSetting() {
			let vm = this

			this.$nextTick(() => {

				let payload = {
					id: this.$store.employee.id,
					send_message_emails: this.$store.employee.send_message_emails,
					send_marketing_emails: this.$store.employee.send_marketing_emails,
					send_service_request_emails: this.$store.employee.send_service_request_emails
				}

				this.saveRecord("User",payload, response => {
					vm.alertSuccess("Email preferences updated")
				})

			})

		},
		/*
		updateOCNSettings(value) {
			let vm = this

			let action = "unsubscribe"
			if (value)
				action = "subscribe"

			let payload = {
				action: action,
				user_id: this.$store.userDetails.id,
				service: "OCN"
			}

			this.postData("User/third-party-settings",payload)
		},
		*/
		updatePrivacySetting(field) {
			let vm = this

			this.$nextTick(() => {
				let payload = {
					id: this.$store.employee.id
				}

				payload[field] = this.$store.employee[field]

				this.postData("User/save",payload,true).then(response => {
					if (response.success)
						vm.alertSuccess("Privacy settings updated")
				})
			})
		},
		changeLock() {
			let vm = this

			this.$nextTick(() => {
				vm.postData("User/save",{id: vm.$store.employee.id,editing_locked: vm.$store.employee.editing_locked},true).then(response => {
					if (response.success)
						vm.alertSuccess("Lock status changed")
				})
			})
		},
		changeAdminLockout() {
			let vm = this

			this.$nextTick(() => {
				vm.postData("User/save",{id: vm.$store.employee.id,admin_lockout: vm.$store.employee.admin_lockout},true).then(response => {
					if (response.success)
						vm.alertSuccess("Admin lock status changed")
				})
			})
		},
		logoutAll() {
			let vm = this

			this.postData("User/save",{id:this.$store.userDetails.id,one_signal_user_id: ""}).then(response => {
				if (response.success) {

					this.postData("User/refresh-api-key",{}).then(response2 => {
						
						this.alerts = []

						this.$store.userDetails = {}
						this.clearStore()

						clearInterval(this.$store.sessionTimer)
						clearInterval(this.$store.messagePollingTimer)
						clearInterval(this.$store.userActiveTimer)

						localStorage.clear()

						vm.$router.push("/")
						vm.$bus.$emit("logout")

					})
				}
			})
		},
		newAuthToken() {
			this.authTokenEditModal.visible = true
			this.authTokenEditModal.payload.label = ""
			this.authTokenEditModal.scope = []
			this.authTokenEditModal.fullScope = true

			if (this.authTokenEditModal.resources.length == 0)
				this.fetchResources()
		},
		saveAuthToken() {
			let vm = this

			let payload = {
				created_by_user: 1,
				label: vm.authTokenEditModal.payload.label,
				scope: JSON.stringify(vm.authTokenEditModal.scope),
				expiry_hrs: this.moment().add(5,"years").diff(this.moment(), "hours")
			}

			if (this.authTokenEditModal.fullScope)
				payload.scope = JSON.stringify(vm.authTokenEditModal.resources.map(resource => resource.name))

			this.postData("UserToken/create",payload,true).then(response => {
				if (response.success) {
					vm.fetchCreatedUserTokens()
					vm.alertSuccess("New Authentication Token created")
					vm.authTokenEditModal.visible = false
				}
			})
		},
		fetchCreatedUserTokens() {
			let vm = this

			let params = {
				filters: [
					["created_by_user",1]
				]
			}

			this.getJSON("UserToken/get",params).then(response => {
				if (response.success)
					vm.appAuthTokens = response.data
			})
		},
		fetchResources() {
			let vm = this

			let params = {
				fields: ["name"]
			}

			this.getJSON("Module/get-resources",params).then(response => {
				if (response.success)
					vm.authTokenEditModal.resources = response.data
			})
		}
		/*
		fetchThirdPartySettings() {
			let vm = this

			let params = {
				action: "get",
				user_id: this.$store.userDetails.id,
				service: "all"
			}

			this.postData("User/third-party-settings",params).then(response => {
				if (response.success) {
					vm.subscriptions = response.data
				}
			})
		}
		*/
	},
	created() {
		let vm = this

		//this.fetchThirdPartySettings()
		this.fetchCreatedUserTokens()
		this.fetchResources()

		navigator.geolocation && navigator.permissions.query({name:'geolocation'}).then(status => {
			if (status.state == 'granted')
				vm.locationEnabled = true
			else
				vm.locationEnabled = false
		})

		if (typeof OneSignal != 'undefined') {
			/*OneSignal.getUserId().then(deviceId => {
				vm.deviceId = deviceId
			})*/
			OneSignal.getExternalUserId().then(id => {
				vm.registeredOneSignalExternalId = id
			})
		}
	}

}
</script>

<style type="text/css">
	h2 button {
		float: right;
	}

	.onesignal-customlink-subscribe {
		background-color: #007bff!important;
		border-radius: 0!important;
		box-shadow: none!important;
		text-transform: none!important;
		font-weight: normal!important;
		font-size: .875rem!important;
		padding: .25rem .5rem!important;
		line-height: 1.5!important;
	}

	@media (max-width: 500px) {
		h2 button {
			float: none;
		}
	}
</style>